import { loadStripe } from '@stripe/stripe-js'
import { THREE_D_S_CARD_TYPES } from 'components/Upgrade/Constants'
import getConfig from 'next/config'
import { FieldError } from 'react-hook-form'
import { trackEvent } from './Analytics'

interface Plan {
  price: number
  stripeCoupon?: { couponedPrice: number }
  discounted_from_price?: number
}

interface StripeError {
  name: string
  error?: { message: string }
  errors?: { message?: string }
}

const {
  publicRuntimeConfig: { CGC_WEB_STRIPE_PUBLIC_KEY },
} = getConfig()

export const getStripePromise = () => loadStripe(CGC_WEB_STRIPE_PUBLIC_KEY)

export const getFullPriceForPlan = (plan?: Plan) => plan?.discounted_from_price || plan?.price

export const getSalePriceForPlan = (plan?: Plan) => {
  const hasStripeCoupon = !!plan?.stripeCoupon?.couponedPrice
  const hasDiscount = !!plan?.discounted_from_price

  if (hasStripeCoupon) {
    return plan.stripeCoupon.couponedPrice
  }

  return hasDiscount ? plan?.price : null
}

export interface StripeCardFormErrors {
  [fieldName: string]: FieldError
}
export const convertStipeErrorForInputErrorMessage = ({
  name,
  error,
}: StripeError): StripeCardFormErrors => {
  const hasError = error?.message !== undefined
  return hasError ? { [name]: { type: 'stripe', message: error.message } } : {}
}

export const isValidCardholderName = (value: string) =>
  value.split(' ').filter((token) => !!token).length >= 2

export const createStripeSource = async ({
  stripe,
  cardNumberElement,
  cardholderName,
  postalCode,
}) => {
  const result = await stripe.createSource(cardNumberElement, {
    type: 'card',
    owner: {
      name: cardholderName,
      address: {
        postal_code: postalCode,
      },
    },
  })

  if (result.error) {
    throw new Error(result.error.message)
  }

  return result.source
}

export const purchaseAndReturnRedirectUrl = async ({
  stripe,
  cardNumberElement,
  redirectUrl,
  stripePlan,
  cardholderName,
  postalCode,
  purchaseSource,
  purchaseSubscription,
  flow,
  conversionType,
}) => {
  let url = null

  const source = await createStripeSource({
    stripe,
    cardNumberElement,
    cardholderName,
    postalCode,
  })

  const is3dsSource = THREE_D_S_CARD_TYPES.includes(source?.card?.three_d_secure)

  if (is3dsSource) {
    const result = await purchaseSource({
      variables: {
        input: {
          stripe_plan_id: stripePlan?.id,
          source_id: source?.id,
          flow,
          redirect_url: redirectUrl,
          conversion_type: conversionType,
        },
      },
    })
    url = result?.data?.create_stripe_source?.source?.redirect?.url
  } else {
    await purchaseSubscription({
      variables: {
        input: {
          stripe_plan_id: stripePlan?.id,
          stripe_source: source?.id,
          conversion_type: 'get-started',
          name_on_card: cardholderName,
        },
      },
    })
  }

  return url
}

export const getPaymentReturnUrl = ({ host = '', baseUrl = '', stripePlan, fullName }) => {
  const queryKeyValuePairs = [
    { key: 'stripe_plan_id', value: stripePlan?.stripe_plan_id },
    { key: 'name', value: fullName },
  ]
  const queryMatch = /\?/g
  const hasQuery = queryMatch.test(baseUrl)
  const query = queryKeyValuePairs.reduce((prev, cur) => {
    const prefix = hasQuery || queryMatch.test(prev) ? '&' : '?'
    return (prev += cur.value ? `${prefix}${cur.key}=${encodeURIComponent(cur.value)}` : '')
  }, '')
  return `${host}${baseUrl}${query}`
}

export const getCardholderName = ({ userInput, query }) => {
  const hasUserInputName = !!userInput?.first_name && !!userInput?.last_name
  const hasQueryName = !!query?.name
  if (hasUserInputName) {
    return `${userInput?.first_name} ${userInput?.last_name}`
  } else if (hasQueryName) {
    return `${query?.name}`
  }
}

export const formatStripePrice = (amount: number): string => {
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })
  const amountInDollars = amount / 100
  const isEvenAmount = amountInDollars % 1 !== 0
  let value = currencyFormatter.format(amountInDollars)

  if (!isEvenAmount) {
    const decimalIndex = value.indexOf('.')
    value = value.slice(0, decimalIndex)
  }

  return value.replace('$', '')
}
